import { ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronRight, FileText } from "lucide-react";
import clsx from "clsx";
import { API_URL } from "constants.js";
import { Badge } from "catalyst-ui-kit/badge";
import Currency from "components/format/currency/currency";
import useIsPhone from "features/files/use-is-phone";
import { PresetId, rangeOf } from "features/customer-products/sales-by-period";
import { listMonthNames, useUiLanguage } from "features/posts/utils/newsDates";
import { ProductSalesRow, ProductSalesYear } from "types/ProductSales";
import LoadFailed from "./load-failed";
import { formatQuantity } from "./product-format";
import { useProductSales } from "./use-product-sales";

export type SalesPeriod = Extract<PresetId, "thisYear" | "lastYear" | "last12Months">;

/** The period buttons, left to right. */
const SALES_PERIODS: { id: SalesPeriod; labelKey: string }[] = [
  { id: "thisYear", labelKey: "products_page_period_this_year" },
  { id: "lastYear", labelKey: "products_page_period_last_year" },
  { id: "last12Months", labelKey: "products_page_period_last_12_months" },
];

interface Props {
  companyId: string;
  productId: string;
  period: SalesPeriod;
  onPeriodChange: (period: SalesPeriod) => void;
  /** The day "this year" and "12 months" end on. */
  today: Date;
}

export default function ProductSalesTab({ companyId, productId, period, onPeriodChange, today }: Props) {
  const { t } = useTranslation();
  const range = useMemo(() => rangeOf({ preset: period, customFrom: "", customTo: "" }, today), [period, today]);
  const { state, retry } = useProductSales(companyId, productId, range);
  const data = state.status === "loaded" ? state.data : null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {data && (
            <p className="text-sm text-gray-500" data-testid="product-sales-summary">
              {t("products_page_period_total")}:{" "}
              <span className="font-semibold tabular-nums text-gray-900">
                <Currency value={data.totals.net} isSymbol isDecimal />
              </span>
              {" · "}
              <span className="tabular-nums">{formatQuantity(data.totals.quantity, data.unit)}</span>
            </p>
          )}
          {data && data.unconvertedDocumentCount > 0 && (
            <p className="mt-1 text-xs text-gray-500">
              {t("products_page_unconverted", { value: data.unconvertedDocumentCount })}
            </p>
          )}
        </div>
        <div role="group" aria-label={t("products_page_period")} className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white text-sm max-sm:flex max-sm:w-full">
          {SALES_PERIODS.map((option, index) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={option.id === period}
              onClick={() => onPeriodChange(option.id)}
              className={clsx(
                "px-3.5 py-2.5 max-sm:min-h-11 max-sm:flex-1 max-sm:px-2 lg:py-1.5",
                index > 0 && "border-l border-gray-300",
                option.id === period ? "bg-indigo-600 font-medium text-white" : "text-gray-700 hover:bg-gray-50"
              )}
            >
              {t(option.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {state.status === "loading" && <SalesSkeleton />}
      {state.status === "failed" && <LoadFailed text={t("products_page_sales_failed")} onRetry={retry} />}
      {data && data.years.length === 0 && <p className="text-sm text-gray-500">{t("products_page_no_sales")}</p>}
      {data && data.years.length > 0 && <SalesTree companyId={companyId} years={data.years} unit={data.unit} />}
    </div>
  );
}

function SalesSkeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-3" data-testid="product-sales-skeleton">
      <div className="h-12 rounded-xl bg-gray-100" />
      <div className="h-12 rounded-xl bg-gray-100" />
    </div>
  );
}

/** Years, each holding its months, each holding its invoice rows; the newest year and its newest month start open. */
function SalesTree({ companyId, years, unit }: { companyId: string; years: ProductSalesYear[]; unit: string | null }) {
  const monthNames = listMonthNames(useUiLanguage());
  const isPhone = useIsPhone();

  return (
    <div className="flex flex-col gap-3">
      {years.map((year, yearIndex) => (
        <Disclosure key={year.year} as="div" defaultOpen={yearIndex === 0} className="rounded-xl border border-gray-200 bg-white">
          <DisclosureButton className="group flex w-full items-center gap-3 px-4 py-3.5 text-left sm:px-5">
            <ChevronRight aria-hidden className="h-4 w-4 text-gray-400 transition-transform group-data-[open]:rotate-90" />
            <span className="font-semibold text-gray-900">{year.year}</span>
            <AmountsCell quantity={formatQuantity(year.quantity, unit)} className="font-semibold">
              <Currency value={year.net} isSymbol isDecimal />
            </AmountsCell>
          </DisclosureButton>
          <DisclosurePanel className="divide-y divide-gray-100 border-t border-gray-100">
            {year.months.map((month, monthIndex) => {
              const name = monthNames[month.month - 1];
              return (
                <Disclosure key={month.month} as="div" defaultOpen={yearIndex === 0 && monthIndex === 0}>
                  <DisclosureButton className="group flex w-full items-center gap-3 py-2.5 pl-4 pr-4 text-left hover:bg-gray-50 sm:pl-9 sm:pr-5">
                    <ChevronRight aria-hidden className="h-3.5 w-3.5 text-gray-400 transition-transform group-data-[open]:rotate-90" />
                    <span className="text-sm font-medium text-gray-800">{name.charAt(0).toUpperCase() + name.slice(1)}</span>
                    <AmountsCell quantity={formatQuantity(month.quantity, unit)} className="text-sm font-medium">
                      <Currency value={month.net} isSymbol isDecimal />
                    </AmountsCell>
                  </DisclosureButton>
                  <DisclosurePanel>
                    <MonthRows companyId={companyId} rows={month.rows} unit={unit} isPhone={isPhone} />
                  </DisclosurePanel>
                </Disclosure>
              );
            })}
          </DisclosurePanel>
        </Disclosure>
      ))}
    </div>
  );
}

/** The quantity and the amount at the right end of a year or month line; on a phone the amount is above the quantity. */
function AmountsCell({ quantity, className, children }: { quantity: string; className: string; children: ReactNode }) {
  return (
    <span className="ml-auto flex flex-col-reverse items-end sm:flex-row sm:items-center sm:gap-3">
      <span className="text-sm tabular-nums text-gray-500">{quantity}</span>
      <span className={clsx("tabular-nums text-gray-900 sm:w-36 sm:text-right", className)}>{children}</span>
    </span>
  );
}

function pdfUrlOf(companyId: string, row: ProductSalesRow): string | null {
  return row.fileId ? `${API_URL}/${companyId}/files/download/${row.fileId}` : null;
}

/** The document number: it opens the PDF, or without a file the customer's documents (the customer card lists both kinds of document). */
function DocumentNumber({ companyId, row }: { companyId: string; row: ProductSalesRow }) {
  const { t } = useTranslation();
  const isReturn = row.kind === "return";
  const pdfUrl = pdfUrlOf(companyId, row);
  const documentsPage = `/app/${companyId}/customers/${row.customer.id}/${isReturn ? "salesreturns" : "invoices"}`;
  const numberClass = "font-medium text-indigo-600 hover:underline";

  return (
    <>
      {pdfUrl ? (
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className={numberClass}>
          {row.number}
        </a>
      ) : (
        <Link to={documentsPage} className={numberClass}>
          {row.number}
        </Link>
      )}
      {isReturn && (
        <Badge color="red" className="ml-2">
          {t("products_page_return")}
        </Badge>
      )}
    </>
  );
}

function PdfIconLink({ companyId, row, className }: { companyId: string; row: ProductSalesRow; className: string }) {
  const { t } = useTranslation();
  const pdfUrl = pdfUrlOf(companyId, row);
  if (!pdfUrl) return null;

  return (
    <a
      href={pdfUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={t("download")}
      aria-label={`${t("download")} ${row.number}`}
      className={clsx("inline-flex rounded text-gray-500 hover:bg-gray-200", className)}
    >
      <FileText aria-hidden className="h-4 w-4" />
    </a>
  );
}

/** A month's documents: a table, or on a phone one card per document (date and number, customer, quantity and amount). */
function MonthRows({
  companyId,
  rows,
  unit,
  isPhone,
}: {
  companyId: string;
  rows: ProductSalesRow[];
  unit: string | null;
  isPhone: boolean;
}) {
  return isPhone ? (
    <MonthCards companyId={companyId} rows={rows} unit={unit} />
  ) : (
    <MonthTable companyId={companyId} rows={rows} unit={unit} />
  );
}

function MonthCards({ companyId, rows, unit }: { companyId: string; rows: ProductSalesRow[]; unit: string | null }) {
  return (
    <ul className="divide-y divide-gray-100 bg-gray-50/60">
      {rows.map((row) => (
        <li key={`${row.kind}-${row.id}`} className={clsx("flex items-center gap-2 py-2 pl-4 pr-1", row.kind === "return" && "text-red-700")}>
          <div className="min-w-0 flex-1 text-sm">
            <p>
              <span className="tabular-nums text-gray-600">{row.date}</span>
              {" · "}
              <DocumentNumber companyId={companyId} row={row} />
            </p>
            <p className="break-words text-gray-800">{row.customer.name}</p>
            <p className="tabular-nums">
              {formatQuantity(row.quantity, unit)}
              {" · "}
              <span className="font-medium">
                <Currency value={row.net} isSymbol isDecimal />
              </span>
            </p>
          </div>
          <PdfIconLink companyId={companyId} row={row} className="p-3" />
        </li>
      ))}
    </ul>
  );
}

function MonthTable({ companyId, rows, unit }: { companyId: string; rows: ProductSalesRow[]; unit: string | null }) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto bg-gray-50/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
            <th className="py-1.5 pl-14 pr-3 font-semibold">{t("date")}</th>
            <th className="px-3 font-semibold">{t("products_page_invoice")}</th>
            <th className="px-3 font-semibold">{t("customer")}</th>
            <th className="px-3 text-right font-semibold">{t("quantity")}</th>
            <th className="px-3 text-right font-semibold">{t("products_page_net_amount")}</th>
            <th className="w-12 pr-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={`${row.kind}-${row.id}`} className={clsx("hover:bg-white", row.kind === "return" && "text-red-700")}>
              <td className="py-2 pl-14 pr-3 tabular-nums text-gray-600">{row.date}</td>
              <td className="px-3">
                <DocumentNumber companyId={companyId} row={row} />
              </td>
              <td className="px-3 text-gray-800">{row.customer.name}</td>
              <td className="px-3 text-right tabular-nums">{formatQuantity(row.quantity, unit)}</td>
              <td className="px-3 text-right font-medium tabular-nums">
                <Currency value={row.net} isSymbol isDecimal />
              </td>
              <td className="pr-3 text-center">
                <PdfIconLink companyId={companyId} row={row} className="p-2.5 lg:p-1.5" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
